import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Check, ArrowLeft, Shield, Star, Sparkles } from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Premium = () => {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const isPremium = user?.subscription_status === "premium";

  const handleSubscribe = async () => {
    setLoading(true);
    
    // Create order
    const orderResult = await apiCall("POST", "/subscription/create-order", { plan: "premium" }, token);
    
    if (!orderResult.success) {
      toast.error(orderResult.error || "Failed to create order");
      setLoading(false);
      return;
    }

    const { order_id, amount, key_id } = orderResult.data;

    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "SIWON HARMONY",
        description: "Premium Subscription - Monthly",
        order_id: order_id,
        handler: async function (response) {
          // Verify payment
          const verifyResult = await apiCall("POST", "/subscription/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          }, token);

          if (verifyResult.success) {
            updateUser({ subscription_status: "premium" });
            toast.success("Welcome to Premium! Full transformation begins.");
            navigate("/dashboard");
          } else {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: user?.name,
          email: user?.email,
        },
        theme: {
          color: "#ffd700",
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    };

    script.onerror = () => {
      toast.error("Failed to load payment gateway");
      setLoading(false);
    };
  };

  const features = [
    { icon: Star, text: "Full 175 trigger access", free: false, premium: true },
    { icon: Sparkles, text: "Personalized Q&A matrix", free: false, premium: true },
    { icon: Crown, text: "Coach assignment + weekly check-ins", free: false, premium: true },
    { icon: Shield, text: "3A Dashboard (read-only)", free: true, premium: true },
    { icon: Check, text: "1 sample trigger deep-dive", free: true, premium: true },
  ];

  return (
    <div className="min-h-screen bg-cosmic-void py-12 px-6" data-testid="premium-page">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto mb-8">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center text-chakra-crown/70 hover:text-divine-gold transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Crown className="w-16 h-16 text-divine-gold mx-auto mb-4" />
          <h1 className="font-heading text-4xl md:text-5xl text-divine-gold tracking-wide">
            UNLOCK FULL TRANSFORMATION
          </h1>
          <p className="text-chakra-crown/70 mt-4 max-w-2xl mx-auto">
            Your sacred journey awaits. Access the complete 3A system with personalized coaching.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Free Trial */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="sacred-card"
          >
            <div className="text-center mb-6">
              <span className="label-sacred">FREE TRIAL</span>
              <p className="text-3xl font-heading text-chakra-crown mt-2">7 Days</p>
            </div>

            <div className="space-y-4 mb-8">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    feature.free ? "bg-chakra-heart/20" : "bg-cosmic-light/30"
                  }`}>
                    {feature.free ? (
                      <Check className="w-4 h-4 text-chakra-heart" />
                    ) : (
                      <span className="text-chakra-crown/30 text-xs">—</span>
                    )}
                  </div>
                  <span className={feature.free ? "text-chakra-crown" : "text-chakra-crown/40"}>
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>

            <Button 
              variant="outline"
              className="w-full vedic-button-secondary"
              disabled
            >
              {user?.subscription_status === "free_trial" ? "Current Plan" : "Trial Expired"}
            </Button>
          </motion.div>

          {/* Premium */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="sacred-card relative overflow-hidden border-divine-gold/40"
          >
            {/* Recommended Badge */}
            <div className="absolute top-4 right-4">
              <span className="px-3 py-1 bg-divine-gold text-cosmic-void text-xs font-bold rounded-full">
                RECOMMENDED
              </span>
            </div>

            <div className="text-center mb-6">
              <span className="label-sacred">PREMIUM</span>
              <div className="flex items-baseline justify-center gap-1 mt-2">
                <span className="text-4xl font-heading text-divine-gold">₹999</span>
                <span className="text-chakra-crown/60">/month</span>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-divine-gold/20 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-divine-gold" />
                  </div>
                  <span className="text-chakra-crown">{feature.text}</span>
                </div>
              ))}
            </div>

            {isPremium ? (
              <Button 
                className="w-full vedic-button"
                disabled
              >
                <Check className="w-5 h-5 mr-2" />
                Active Subscription
              </Button>
            ) : (
              <Button 
                className="w-full vedic-button"
                onClick={handleSubscribe}
                disabled={loading}
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <span className="animate-pulse">Processing...</span>
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    Unlock Premium Now
                  </>
                )}
              </Button>
            )}

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-divine-gold/5 to-transparent pointer-events-none" />
          </motion.div>
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-chakra-crown/50 text-sm">
            Secure payment powered by Razorpay • Cancel anytime • 100% transformation guarantee
          </p>
        </motion.div>

        {/* Testimonial */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 sacred-card text-center"
        >
          <p className="text-chakra-crown text-lg italic mb-4">
            "The 3A journey transformed my relationship with myself first. The triggers I identified
            were mirror reflections I never saw before. Profound."
          </p>
          <p className="text-divine-gold font-medium">— Priya S., Delhi</p>
          <p className="text-chakra-crown/50 text-sm">Premium member since 2024</p>
        </motion.div>
      </div>
    </div>
  );
};

export default Premium;
